(() => {
  const LOAD_MORE_SELECTOR = '.js-load-more';
  const INFINITE_SCROLL_SELECTOR = '.js-infinite-scroll';
  const GRID_SELECTOR = '.load-more-grid';
  const initializedButtons = new WeakSet();
  const spinnerObservers = new WeakMap();

  const getSectionRoot = (trigger) =>
    trigger.closest('.shopify-section') || document;

  const getPaginationState = (trigger) => {
    const root = getSectionRoot(trigger);
    const totalPagesInput = root.querySelector('[data-total-pages]');
    const currentPageInput = root.querySelector('[data-current-page]');
    const nextUrlInput = root.querySelector('[data-next-url]');
    const grid = root.querySelector(GRID_SELECTOR);

    if (!totalPagesInput || !currentPageInput || !nextUrlInput || !grid) {
      return null;
    }

    const totalPages = Number.parseInt(totalPagesInput.value, 10);
    const currentPage = Number.parseInt(currentPageInput.value, 10);

    if (!Number.isFinite(totalPages) || !Number.isFinite(currentPage)) {
      return null;
    }

    return {
      root,
      grid,
      totalPages,
      currentPage,
      currentPageInput,
      nextUrlInput,
    };
  };

  const buildPageUrl = (value, page) => {
    if (!value) return null;

    try {
      const url = new URL(value, window.location.href);
      url.searchParams.set('page', String(page));
      return url.toString();
    } catch (error) {
      return value.replace(/([?&]page=)[0-9]+/i, `$1${page}`);
    }
  };

  const setLoading = (trigger, loading) => {
    trigger.classList.toggle('loading', loading);
    trigger.toggleAttribute('disabled', loading);
    trigger.setAttribute('aria-busy', String(loading));
  };

  const appendResponseItems = (html, grid) => {
    const responseDocument = new DOMParser().parseFromString(html, 'text/html');
    const responseGrid = responseDocument.querySelector(GRID_SELECTOR);

    if (!responseGrid) {
      throw new Error(`No se encontró ${GRID_SELECTOR} en la respuesta.`);
    }

    const fragment = document.createDocumentFragment();
    Array.from(responseGrid.children).forEach((item) => {
      fragment.appendChild(document.importNode(item, true));
    });

    grid.appendChild(fragment);
  };

  const updatePaginationInputs = (state, page, pageUrl) => {
    state.currentPageInput.value = String(page);
    state.currentPageInput.dataset.currentPage = String(page);
    state.nextUrlInput.value = pageUrl;
    state.nextUrlInput.dataset.nextUrl = pageUrl;
  };

  const stopObservingSpinner = (spinner) => {
    const observer = spinnerObservers.get(spinner);
    if (!observer) return;

    observer.disconnect();
    spinnerObservers.delete(spinner);
  };

  const observeSpinner = (spinner) => {
    if (!spinner?.isConnected) return;

    stopObservingSpinner(spinner);

    if (typeof IntersectionObserver !== 'function') {
      loadItems(spinner);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;

        observer.disconnect();
        spinnerObservers.delete(spinner);
        loadItems(spinner);
      },
      { rootMargin: '200px 0px', threshold: 0.01 },
    );

    spinnerObservers.set(spinner, observer);
    observer.observe(spinner);
  };

  const loadItems = async (trigger) => {
    if (!trigger || trigger.dataset.loading === 'true') return;

    const state = getPaginationState(trigger);
    if (!state) {
      console.warn('No fue posible inicializar la paginación incremental.');
      return;
    }

    const nextPage = state.currentPage + 1;
    const nextUrl = buildPageUrl(state.nextUrlInput.value, nextPage);

    if (!nextUrl || nextPage > state.totalPages) {
      stopObservingSpinner(trigger);
      trigger.remove();
      return;
    }

    trigger.dataset.loading = 'true';
    setLoading(trigger, true);

    try {
      const response = await fetch(nextUrl, {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });

      if (!response.ok) {
        throw new Error(`La solicitud devolvió HTTP ${response.status}.`);
      }

      appendResponseItems(await response.text(), state.grid);
      updatePaginationInputs(state, nextPage, nextUrl);

      if (typeof window.colorSwatches === 'function') {
        window.colorSwatches();
      }

      document.dispatchEvent(
        new CustomEvent('theme:pagination:loaded', {
          detail: { page: nextPage, url: nextUrl, root: state.root },
        }),
      );

      if (nextPage >= state.totalPages) {
        stopObservingSpinner(trigger);
        trigger.remove();
        return;
      }
    } catch (error) {
      console.error('No fue posible cargar más elementos.', error);
    } finally {
      if (trigger.isConnected) {
        delete trigger.dataset.loading;
        setLoading(trigger, false);

        if (trigger.matches(INFINITE_SCROLL_SELECTOR)) {
          observeSpinner(trigger);
        }
      }
    }
  };

  const initializeLoadMoreButton = (button) => {
    if (initializedButtons.has(button)) return;

    initializedButtons.add(button);
    button.addEventListener('click', () => loadItems(button));
  };

  const initialize = (root = document) => {
    root.querySelectorAll?.(LOAD_MORE_SELECTOR).forEach(initializeLoadMoreButton);
    root.querySelectorAll?.(INFINITE_SCROLL_SELECTOR).forEach(observeSpinner);

    if (root.matches?.(LOAD_MORE_SELECTOR)) initializeLoadMoreButton(root);
    if (root.matches?.(INFINITE_SCROLL_SELECTOR)) observeSpinner(root);
  };

  const destroy = (root) => {
    root.querySelectorAll?.(INFINITE_SCROLL_SELECTOR).forEach(
      stopObservingSpinner,
    );

    if (root.matches?.(INFINITE_SCROLL_SELECTOR)) {
      stopObservingSpinner(root);
    }
  };

  window.loadMore = initialize;

  document.addEventListener('shopify:section:load', (event) => {
    initialize(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    destroy(event.target);
  });

  initialize();
})();
